/**
 * /admin/books — the editable books of accounts.
 * @file src/web/features/admin/pages/books.tsx
 *
 * Data-entry home (mirrors Tally's vouchers/masters). Day Book & Ledgers
 * are editable here; "+ New" creates manual vouchers/ledgers that are
 * protected from re-import. Reports (Trial Balance, Balance Sheet, …) live
 * separately under /admin/reports.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReceiptText, BookOpenText, Plus, ArrowRight } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { useCompany } from '../lib/company-context';
import { VoucherModal } from '../components/VoucherModal';
import { LedgerModal } from '../components/LedgerModal';

export default function BooksPage() {
  const { entityId } = useCompany();
  const [modal, setModal] = useState<'voucher' | 'ledger' | null>(null);

  return (
    <>
      <SEO title="Books" />
      <AdminPageHeader title="Books" breadcrumbs={[{ label: 'oddits' }, { label: 'Books' }]} />

      <p className="mb-5 text-sm text-ink-500">Your vouchers and ledgers — imported from Tally and editable here. Manual entries and edits are kept safe when you re-import.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card
          icon={ReceiptText} title="Day Book" desc="All vouchers — open one to view or edit"
          href="/admin/daybook" onNew={() => setModal('voucher')} newLabel="New voucher"
        />
        <Card
          icon={BookOpenText} title="Ledgers" desc="All ledgers — open one for its statement"
          href="/admin/ledgers" onNew={() => setModal('ledger')} newLabel="New ledger"
        />
      </div>

      {modal === 'voucher' && <VoucherModal entityId={entityId} voucherId={null} onClose={() => setModal(null)} onSaved={() => setModal(null)} />}
      {modal === 'ledger' && <LedgerModal entityId={entityId} ledger={null} onClose={() => setModal(null)} onSaved={() => setModal(null)} />}
    </>
  );
}

function Card({ icon: Icon, title, desc, href, onNew, newLabel }: { icon: any; title: string; desc: string; href: string; onNew?: () => void; newLabel?: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <span className="grid size-10 place-items-center rounded-xl bg-highlighter-40 text-inkblack"><Icon className="h-5 w-5" /></span>
        {onNew && <button onClick={onNew} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-3.5 py-1.5 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> {newLabel}</button>}
      </div>
      <p className="mt-4 text-sm font-semibold text-ink-900">{title}</p>
      <p className="mt-1 text-xs text-ink-500">{desc}</p>
      <Link to={href} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-inkblack no-underline hover:text-inkblack">Open {title.toLowerCase()} <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}
